import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/hooks/use-api'

export type AdminOverview = {
  counts: {
    users: number
    leads: number
    campaigns: number
    posts: number
    messages: number
    connected_accounts: number
  }
  plans: Array<{ plan: string; n: number }>
  comp_count?: number
  recent_activity_24h: Array<{ action_type: string; n: number }>
}

export type AdminUserRow = {
  id: string
  email: string
  full_name: string | null
  plan: string
  plan_status: string
  /** 0/1 friends-and-family override (migration 0024). When 1, plan tier is
   * effectively 'agency' regardless of `plan`, and Stripe is bypassed. */
  is_comp?: number
  comp_granted_by?: string | null
  comp_granted_at?: string | null
  stripe_subscription_id?: string | null
  stripe_customer_id?: string | null
  created_at: string
  timezone: string | null
  leads_count: number
  campaigns_count: number
  posts_count: number
  sent_count: number
  received_count: number
  last_activity_at: string | null
  linkedin_name: string | null
  unipile_account_id: string | null
}

export type AdminLeadRow = {
  id: string
  user_id: string
  full_name: string | null
  first_name: string | null
  last_name: string | null
  linkedin_url: string
  company: string | null
  title: string | null
  status: string
  icp_score: number | null
  created_at: string
  owner_email: string
  owner_name: string | null
}

export type AdminCampaignRow = {
  id: string
  user_id: string
  name: string
  status: string
  daily_limit: number
  stats_sent: number
  stats_replied: number
  stats_meetings: number
  leads_count: number
  created_at: string
  updated_at: string
  owner_email: string
  owner_name: string | null
}

export type AdminPostRow = {
  id: string
  user_id: string
  content: string
  topic: string | null
  content_type: string
  status: string
  scheduled_for: string | null
  published_at: string | null
  linkedin_post_id: string | null
  metrics_likes: number
  metrics_comments: number
  created_at: string
  owner_email: string
  owner_name: string | null
}

export type AdminConversationRow = {
  user_id: string
  lead_id: string
  lead_name: string | null
  lead_linkedin_url: string
  lead_status: string
  lead_company: string | null
  owner_email: string
  owner_name: string | null
  last_message_at: string
  inbound_count: number
  outbound_count: number
  total_messages: number
}

export type AdminMessageRow = {
  id: string
  user_id: string
  lead_id: string
  campaign_id: string | null
  direction: 'inbound' | 'outbound'
  message_type: string
  content: string
  sent_at: string | null
  created_at: string
  status: string | null
  sentiment: string | null
}

const POLL_MS = 30_000

export function useAdminOverview() {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: async () => {
      const res = await api<AdminOverview>('/api/admin/overview')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    refetchInterval: POLL_MS,
  })
}

export function useAdminUsers() {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const res = await api<{ users: AdminUserRow[] }>('/api/admin/users')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.users
    },
    refetchInterval: POLL_MS,
  })
}

export function useAdminLeads(filterUserId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'leads', filterUserId],
    queryFn: async () => {
      const path = filterUserId
        ? `/api/admin/leads?user_id=${encodeURIComponent(filterUserId)}`
        : '/api/admin/leads'
      const res = await api<{ leads: AdminLeadRow[] }>(path)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.leads
    },
    refetchInterval: POLL_MS,
  })
}

export function useAdminCampaigns(filterUserId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'campaigns', filterUserId],
    queryFn: async () => {
      const path = filterUserId
        ? `/api/admin/campaigns?user_id=${encodeURIComponent(filterUserId)}`
        : '/api/admin/campaigns'
      const res = await api<{ campaigns: AdminCampaignRow[] }>(path)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.campaigns
    },
    refetchInterval: POLL_MS,
  })
}

export function useAdminContent(filterUserId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'content', filterUserId],
    queryFn: async () => {
      const path = filterUserId
        ? `/api/admin/content?user_id=${encodeURIComponent(filterUserId)}`
        : '/api/admin/content'
      const res = await api<{ posts: AdminPostRow[] }>(path)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.posts
    },
    refetchInterval: POLL_MS,
  })
}

export function useAdminInbox(filterUserId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'inbox', filterUserId],
    queryFn: async () => {
      const path = filterUserId
        ? `/api/admin/inbox?user_id=${encodeURIComponent(filterUserId)}`
        : '/api/admin/inbox'
      const res = await api<{ conversations: AdminConversationRow[] }>(path)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.conversations
    },
    refetchInterval: POLL_MS,
  })
}

// ---------------------------------------------------------------------------
// API Spend — backs the /super-admin/spend page.
//
// Reads three D1 ledgers via the leads worker's /admin/spend route:
//   - ai_spend_ledger      — @dealcloser/ai-gateway writes one row per
//                            Anthropic / OpenAI / Perplexity call
//   - unipile_spend_ledger — @dealcloser/unipile-gateway writes one row per
//                            Unipile call (cron + send paths + search)
//   - apollo_spend_ledger  — clients/apollo.ts records bulk_match credits
//
// Pre-launch caps total $15/day across the four USD-billed services:
//   Anthropic $5 + OpenAI $2 + Perplexity $4 + Unipile $4
// Apollo bills monthly in credits (default 2500/mo = Basic plan ceiling).
//
// Polling every 30s so the page acts as a live monitor during the F&F beta.
// Each poll is bounded to ~10 small D1 aggregate queries on the worker side
// — well under the ledger overhead it's monitoring.
// ---------------------------------------------------------------------------

export type AdminSpendBreakdownRow = {
  source: string
  usd: number
  calls: number
}

export type AdminSpendUserRow = {
  user_id: string | null
  usd: number
  calls: number
}

export type AdminServiceCard = {
  service: 'anthropic' | 'openai' | 'perplexity'
  /** Always 'usd' — every LLM provider is USD-capped. Unipile was removed
   *  from this module 2026-06-10 (subscription-billed, separate concern). */
  throttle_kind: 'usd'
  today_usd: number
  cap_usd: number
  headroom_usd: number
  pct_used: number
  /** Optional billing-model explainer shown under the card title. */
  billing_note?: string
  by_source: AdminSpendBreakdownRow[]
  by_user: AdminSpendUserRow[]
}

export type AdminPerUserDailyRow = {
  user_id: string | null
  email: string
  plan: string
  is_comp: number
  anthropic_usd: number
  openai_usd: number
  perplexity_usd: number
  total_usd: number
  ai_calls: number
  daily_cap_usd: number
  pct_used: number
}

export type AdminOrgSummary = {
  total_daily_spend_usd: number
  total_daily_limit_usd: number
  total_daily_headroom_usd: number
  pct_used: number
  user_count: number
  default_per_user_cap_usd: number
}

export type AdminApolloLiveHealth = {
  credits_used: number | null
  credits_limit: number | null
  credits_remaining: number | null
  plan_name: string | null
  fetched_at: string
}

export type AdminSpendApolloCard = {
  credits_today: number
  credits_mtd: number
  cap_credits_monthly: number
  headroom_mtd: number
  pct_used: number
  by_user: Array<{ user_id: string | null; credits: number }>
  /** Live Apollo account state from GET /v1/auth/health. Null when the
   *  endpoint failed or APOLLO_API_KEY is unset; UI then falls back to
   *  the local ledger-based figures (credits_mtd / headroom_mtd). */
  live?: AdminApolloLiveHealth | null
  /** Sum of per-user Apollo monthly quotas (user_settings.apollo_monthly_quota_credits).
   *  Shown next to the cap so staff can spot over-allocation. */
  assigned_quota_credits?: number
  /** True when assigned_quota_credits > cap_credits_monthly — drives the
   *  red over-allocation warning banner on /super-admin/spend. */
  assigned_quota_over_cap?: boolean
  /** Sum of every user's CURRENT unspent credit balance. Each of these,
   *  when spent on enrichment, consumes one Apollo credit — so they are
   *  committed even though not yet used. */
  assigned_unspent_credits?: number
  /** credits_mtd + assigned_unspent_credits. */
  committed_credits?: number
  /** cap - used MTD - assigned unspent. NEGATIVE = over-committed. */
  uncommitted_headroom?: number
}

export type AdminFixedCost = {
  id: string
  name: string
  category: 'infrastructure' | 'api' | 'tooling' | 'usage-based'
  usdPerMonth: number
  /** false = estimated from public pricing, not confirmed against an invoice. */
  verified: boolean
  note: string
}

export type AdminCosts = {
  items: AdminFixedCost[]
  fixed_monthly_usd: number
  ai_mtd_usd: number
  /** MTD usage scaled to a full month so it can be added to subscriptions. */
  ai_projected_monthly_usd: number
  /** fixed + projected usage = what a full month actually costs. */
  total_run_rate_usd: number
  /** True while any line is still an estimate rather than a real invoice. */
  estimated: boolean
  unverified_count: number
  paying_users: number
  mrr_usd: number
  /** null when there are no paying customers to divide by. */
  cost_per_paying_user_usd: number | null
  gross_margin_usd: number | null
}

export type AdminSpendData = {
  day: string
  /** Sum of today_usd across all four USD-billed services. */
  total_usd: number
  /** Sum of the four per-service caps — what the org is allowed to spend today. */
  total_cap_usd: number
  total_headroom_usd: number
  services: AdminServiceCard[]
  apollo: AdminSpendApolloCard
  /** Per-user breakdown for the target day, sorted by total_usd desc.
   *  Includes ALL services (Anthropic, OpenAI, Perplexity, Unipile). */
  by_user_daily: AdminPerUserDailyRow[]
  /** Org-wide grand totals + per-user-cap aggregation. */
  org_summary: AdminOrgSummary
  /** Monthly cost of the tools and APIs the product runs on, plus what that
   *  works out to per paying customer. Optional so an older worker deploy
   *  does not break the page. */
  costs?: AdminCosts
  // Legacy single-service shape preserved for back-compat. New code should
  // read `services` + `apollo`; these fields refer to Claude only and will
  // be removed once the dashboard fully cuts over.
  today_usd: number
  cap_usd: number
  headroom_usd: number
  by_source: AdminSpendBreakdownRow[]
  by_user: AdminSpendUserRow[]
}

export function useAdminSpend(day?: string) {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'spend', day ?? 'today'],
    queryFn: async () => {
      const path = day
        ? `/api/admin/spend?day=${encodeURIComponent(day)}`
        : '/api/admin/spend'
      const res = await api<AdminSpendData>(path)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    refetchInterval: POLL_MS,
  })
}

export function useAdminInboxMessages(leadId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'inbox', 'messages', leadId],
    queryFn: async () => {
      if (!leadId) return [] as AdminMessageRow[]
      const res = await api<{ messages: AdminMessageRow[] }>(
        `/api/admin/inbox/${encodeURIComponent(leadId)}/messages`,
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.messages
    },
    enabled: Boolean(leadId),
  })
}

// ---------------------------------------------------------------------------
// Comp toggle (migration 0024 — friends-and-family override).
// POST /admin/users/:id/comp { enabled }
//   enabled=true: flips is_comp=1 and tops credits up to the stored plan's
//                 monthly allocation (Basic 100 / Pro 200), never beyond.
//   enabled=false: clears the flag; users.plan/plan_status are left
//                  untouched (so the user reverts to whatever they had).
//
// Stripe-subscription cancellation is intentionally NOT done here — the
// end user (or KNWN4 staff via stripe.com Dashboard) cancels themselves.
// The comp guard in the webhooks worker short-circuits the cancellation
// event so Unipile stays connected and plan_status doesn't flip.
//
// The user-facing dashboard does NOT change for comp users — they appear
// as a normal Agency seat (no "COMP" badge, no F&F card).
// ---------------------------------------------------------------------------

export type CompToggleResponse = {
  user_id: string
  is_comp: 0 | 1
  granted_by?: string
  previous_plan?: string
  previous_status?: string
  previous_subscription_id?: string | null
  reverted_to_plan?: string
  reverted_to_status?: string
  credits_allocated?: number
}

export function useToggleComp() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { userId: string; enabled: boolean }) => {
      const res = await api<CompToggleResponse>(
        `/api/admin/users/${encodeURIComponent(vars.userId)}/comp`,
        { method: 'POST', body: { enabled: vars.enabled } },
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] })
      qc.invalidateQueries({ queryKey: ['admin', 'overview'] })
    },
  })
}

export type AdminCompAuditRow = {
  id: string
  action_type: 'comp_granted' | 'comp_revoked'
  details: string
  created_at: string
}

export function useAdminCompAudit(userId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'comp-audit', userId],
    queryFn: async () => {
      if (!userId) return [] as AdminCompAuditRow[]
      const res = await api<{ audit: AdminCompAuditRow[] }>(
        `/api/admin/users/${encodeURIComponent(userId)}/comp/audit`,
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.audit
    },
    enabled: Boolean(userId),
  })
}

// ---------------------------------------------------------------------------
// Free-month promo codes (webhooks worker, /api/billing/admin/*).
//
// Staff generate single-use Stripe promotion codes on demand. A redeeming
// customer gets their first month free; during that month the AI router
// serves them the Basic-tier model for message generation even on Pro
// (user_settings.basic_model_until). Everything else about their plan is
// unchanged.
// ---------------------------------------------------------------------------

export type FreeMonthCodeRow = {
  id: string
  code: string
  active: boolean
  times_redeemed: number
  max_redemptions: number | null
  created_at: string
}

export function useFreeMonthCodes() {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'free-month-codes'],
    queryFn: async () => {
      const res = await api<{ codes: FreeMonthCodeRow[] }>('/api/billing/admin/free-month-codes')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.codes
    },
  })
}

export function useGenerateFreeMonthCode() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { code?: string }) => {
      const res = await api<{ code: string; promotion_code_id: string }>(
        '/api/billing/admin/free-month-code',
        { method: 'POST', body: vars.code ? { code: vars.code } : {} },
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'free-month-codes'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Orchestrator observability (in-backend engine traces, migration 0026)
// ---------------------------------------------------------------------------

export type OrchestratorRun = {
  id: string
  started_at: string
  finished_at: string | null
  mode: string
  trigger: string
  considered: number
  emitted: number
  sent: number
  blocked: number
  deferred: number
  failed: number
  duration_ms: number | null
  error: string | null
}

export type OrchestratorRunsResponse = {
  runs: OrchestratorRun[]
  last_hour: {
    runs: number
    sent: number
    blocked: number
    deferred: number
    failed: number
  } | null
}

export type OrchestratorEvent = {
  id: string
  ts: string
  user_id: string | null
  campaign_id: string | null
  lead_id: string | null
  step_type: string | null
  phase: string
  outcome: string
  detail: string | null
  duration_ms: number | null
  lead_name: string | null
  campaign_name: string | null
}

export function useOrchestratorRuns() {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'orchestrator', 'runs'],
    queryFn: async () => {
      const res = await api<OrchestratorRunsResponse>('/api/admin/orchestrator/runs?limit=60')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    refetchInterval: 30_000,
  })
}

export function useOrchestratorRunEvents(runId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'orchestrator', 'events', runId],
    queryFn: async () => {
      const res = await api<{ events: OrchestratorEvent[] }>(
        `/api/admin/orchestrator/runs/${runId}/events`,
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.events
    },
    enabled: runId !== null,
  })
}

export type LifecycleState = {
  user_id: string
  stage: string
  entered_at: string
  updated_at: string
  email: string
  full_name: string | null
  plan: string
  plan_status: string
  signed_up_at: string
  utm_source: string | null
  utm_campaign: string | null
  referrer: string | null
}

export type LifecycleEmail = {
  user_id: string
  email_key: string
  sent_at: string
  email: string
}

export type CapturedLead = {
  id: string
  email: string
  source: string
  projected_revenue: number | null
  avg_deal_value: number | null
  calls_per_month: number | null
  utm_source: string | null
  emailed_at: string | null
  created_at: string
  converted: number
}

export type LifecycleResponse = {
  states: LifecycleState[]
  emails: LifecycleEmail[]
  stage_counts: { stage: string; n: number }[]
  source_counts: { source: string; n: number }[]
  captured_leads: CapturedLead[]
}

export function useLifecycle() {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'lifecycle'],
    queryFn: async () => {
      const res = await api<LifecycleResponse>('/api/admin/lifecycle')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    refetchInterval: 60_000,
  })
}

// ---------------------------------------------------------------------------
// SEO monitor — backs the /super-admin/seo tab. Rows are written by the daily
// crawl (seo/monitor.py) via POST /campaigns/internal/seo/run, so this reads
// our own D1 rather than reaching out to GitHub.
// ---------------------------------------------------------------------------

export type SeoIssue = {
  severity: 'high' | 'medium' | 'low'
  page: string
  issue: string
}

export type SeoPageInfo = {
  title_len: number
  desc_len: number
  h1_count: number
  words: number
  jsonld_blocks: number
  jsonld_invalid: number
  images: number
  images_no_alt: number
}

export type SeoTarget = {
  keyword: string
  path: string
  intent: string
  status: string
  why?: string
}

export type SeoRunSummary = {
  ran_at: string
  pages_crawled: number
  high: number
  medium: number
  low: number
  new_count: number
  fixed_count: number
}

export type SeoLatestRun = SeoRunSummary & {
  id: string
  issues: SeoIssue[]
  pages: Record<string, SeoPageInfo>
  targets_todo: SeoTarget[]
  report_md: string | null
}

export type SeoResponse = {
  latest: SeoLatestRun | null
  /** Oldest first, so a chart reads left to right. */
  history: SeoRunSummary[]
}

export function useAdminSeo() {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'seo'],
    queryFn: async () => {
      const res = await api<SeoResponse>('/api/admin/seo')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    // The crawl runs once a day, so polling hard would be pointless.
    refetchInterval: 5 * 60_000,
  })
}
